import { useState, useEffect } from "react";
import { Bell, X } from "lucide-react";
import { clsx } from "clsx";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";

export default function NotificationHistoryProm() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalNotification, setModalNotification] = useState(null); // Estado del modal

  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setNotifications([]);
        setLoading(false);
        setError("No hay usuario autenticado");
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) {
          setError("No se encontró la información del usuario");
          setLoading(false);
          return;
        }

        const promoProfilesSnap = await getDocs(
          collection(db, "users", user.uid, "promoProfiles")
        );
        const promoMap = {};
        promoProfilesSnap.docs.forEach((d) => {
          const data = d.data();
          promoMap[d.id] = data.name || "Perfil promocional";
        });
        const userIdsToFilter = [user.uid, ...Object.keys(promoMap)];

        const batches = [];
        const tempIds = [...userIdsToFilter];
        while (tempIds.length) batches.push(tempIds.splice(0, 10));

        const unsubscribes = batches.map((batchIds) => {
          const q = query(
            collection(db, "NotificationHistory"),
            where("userId", "in", batchIds),
            orderBy("timestamp", "desc"),
            limit(50)
          );

          return onSnapshot(
            q,
            (snapshot) => {
              const notifs = snapshot.docs.map((doc) => {
                const data = doc.data();
                const profileName = promoMap[data.userId] || null;
                return {
                  id: doc.id,
                  type: data.type || "message",
                  title: data.title || "Mensaje recibido",
                  description: data.body || "Sin contenido",
                  timestamp: data.timestamp ? data.timestamp.toDate() : null,
                  url: data.url || `/chat/${data.userId}`,
                  read: data.read || false,
                  profileName,
                };
              });

              setNotifications((prev) => {
                const merged = [...prev, ...notifs];
                return merged.reduce((acc, curr) => {
                  if (!acc.find((x) => x.id === curr.id)) acc.push(curr);
                  return acc;
                }, []);
              });

              setLoading(false);
              setError(null);
            },
            (error) => {
              console.error("Error obteniendo notificaciones:", error);
              setError("Error al cargar notificaciones");
              setLoading(false);
            }
          );
        });

        return () => unsubscribes.forEach((u) => u());
      } catch (err) {
        console.error("Error al obtener info del usuario:", err);
        setError("Error al cargar la información del usuario");
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, [auth, db]);

  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, "NotificationHistory", notificationId);
      await updateDoc(notificationRef, { read: true });
    } catch (error) {
      console.error("Error al marcar notificación como leída:", error);
    }
  };

 const handleNotificationClick = async (notification) => {
  // Marcar como leída primero
  await markAsRead(notification.id);

  // Actualizar estado local inmediatamente
  setNotifications((prev) =>
    prev.map((n) =>
      n.id === notification.id ? { ...n, read: true } : n
    )
  );

  // Abrir el modal
  setModalNotification(notification);
};


  const closeModal = () => setModalNotification(null);

  const goToAdminProfile = () => {
    if (modalNotification) {
      // Aquí puedes definir la ruta administrativa del perfil promocional
      // Por ejemplo, si tienes userId o profileId:
      navigate(`/admin/promotional/${modalNotification.userId}`);
      closeModal();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "Fecha desconocida";
    const now = new Date();
    const diff = (now - timestamp) / 1000;
    if (diff < 60) return "hace unos segundos";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} horas`;
    return timestamp.toLocaleDateString("es-DO", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Historial de notificaciones"
      >
        <Bell className="w-6 h-6" />
        {notifications.some((n) => n.type === "message" && !n.read) && (
          <span className="absolute top-0 right-0 block w-3 h-3 bg-red-500 rounded-full ring-2 ring-white dark:ring-gray-800 animate-pulse" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-full sm:max-w-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-50">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Mensajes
            </h3>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-500 hover:text-red-500 transition focus:outline-none"
              aria-label="Cerrar panel de notificaciones"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400 animate-pulse">
              Cargando mensajes...
            </div>
          ) : error ? (
            <div className="p-4 text-center text-sm text-red-500 dark:text-red-400">
              {error}
            </div>
          ) : notifications.filter((n) => n.type === "message").length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
              No hay mensajes.
            </div>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
              {notifications
                .filter((n) => n.type === "message")
                .map((n) => (
                  <li
                    key={n.id}
                    className={clsx(
                      "px-4 py-3 transition cursor-pointer flex flex-col space-y-1",
                      n.read
                        ? "bg-gray-50 dark:bg-gray-900 opacity-75"
                        : "bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                    )}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div
                          className={clsx(
                            "w-2 h-2 rounded-full mt-1",
                            n.read ? "bg-gray-400" : "bg-blue-500 animate-pulse"
                          )}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {n.title}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                          {n.description}
                        </p>
                      </div>
                    </div>
                    {n.profileName && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 ml-5">
                        Perfil: {n.profileName}
                      </span>
                    )}
                    <div className="flex justify-between items-center mt-1 ml-5">
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {formatTime(n.timestamp)}
                      </span>
                      <span className="text-xs font-medium text-green-500">
                        mensaje
                      </span>
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      {/* Modal */}
      {modalNotification && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl w-96 shadow-lg">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Notificación
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Para ver los mensajes, por favor accede a la administración de tu perfil promocional.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cerrar
              </button>
              
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
